import { useMutation, useQuery } from "@apollo/client";
import { XIcon } from "@heroicons/react/outline";
import { isEqual, uniqBy } from "lodash";
import { useState } from "react";
import {
  AssessmentNominationFragment,
  GetAssessmentProgramNomineesQuery,
  GetAssessmentProgramNomineesQueryVariables,
  SaveAssessmentNomineesMutation,
  SaveAssessmentNomineesMutationVariables,
} from "types/graphql-schema";

import getAssessmentProgramNomineesQuery from "@apps/assessments/graphql/get-assessment-program-nominees-query";
import saveAssessmentNomineesMutation from "@apps/assessments/graphql/save-assessment-nominees-mutation";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import UserComboboxDeactivatedIcon from "@components/user-combobox/user-combobox-deactivated-icon";
import { UserComboboxOption } from "@components/user-combobox/user-combobox-list";
import { assertEdgesNonNull } from "@helpers/helpers";
import { pluralize } from "@helpers/string";

// The server refuses a save with the same texts as the AI chat
// (backend/compliance/peer_nominations.py). Each one says what is wrong, so it
// is shown as it is, and not reported as an unexpected error.
const nominationRefusals = [
  "not open for peer nominations",
  "not part of the selected cycle",
  "selected automatically for this review",
  "You cannot select peer reviewers for this person",
  "may be selected only once",
  "cannot nominate themselves",
  "not eligible in this organization",
].map((match) => ({
  match,
  title: "Nominations not saved",
  showMessage: true,
}));

// A nominee is read from the nomination row, not from the user: a person removed
// from the organization is no longer a readable user, but stays a nominee until
// the manager removes them. The save then sends every nominee's id.
type Nominee = {
  id: number;
  name: string;
  avatar?: string | null;
  // Null when the person cannot be read any more.
  activeAssessmentNominationCount: number | null;
  deactivated: boolean;
};

const AssessmenNominationtDialog = ({
  assessmentNomination,
  onClose,
  onSaved,
}: {
  assessmentNomination: AssessmentNominationFragment;
  onClose: () => void;
  // Called after a save, before onClose. onClose alone also runs on Close and X.
  onSaved?: () => void;
}) => {
  const [initialNominees, setInitialNominees] = useState<Nominee[]>([]);
  const [nominees, setNominees] = useState<Nominee[]>([]);
  // A save replaces every nomination with the list it sends. Until the current
  // list has loaded, the dialog has nothing safe to send, so it cannot save.
  const [loaded, setLoaded] = useState(false);

  const { loading, error } = useQuery<
    GetAssessmentProgramNomineesQuery,
    GetAssessmentProgramNomineesQueryVariables
  >(getAssessmentProgramNomineesQuery, {
    fetchPolicy: "network-only",
    variables: {
      programId: assessmentNomination.complianceProgram.id,
      targetId: assessmentNomination.targetUser.id,
    },
    onCompleted: (response) => {
      const newNominees = uniqBy(
        response?.complianceProgram?.nominationsForUser
          ? assertEdgesNonNull(response?.complianceProgram?.nominationsForUser)
          : [],
        (nomination) => nomination.responderId,
      ).map((nomination) => ({
        id: nomination.responderId,
        name: nomination.responderName,
        avatar: nomination.responder?.avatar,
        activeAssessmentNominationCount:
          nomination.responder?.activeAssessmentNominationCount ?? null,
        deactivated: nomination.responderDeactivated,
      }));
      setNominees(newNominees);
      setInitialNominees(newNominees);
      setLoaded(true);
    },
  });

  const nomineeResponderIds = nominees.map((nominee) => nominee.id);
  const nomineesWithoutTarget = nominees.filter(
    (nominee) => nominee.id !== assessmentNomination.targetUser.id,
  );

  const {
    options,
    loading: comboboxLoading,
    query,
    setQuery,
  } = useUserComboboxQuery({
    excludeUserIds: nomineeResponderIds.concat(
      assessmentNomination.targetUser.id,
    ),
    showActiveAssessmentNominationCount: true,
  });

  const [saveAssessmentNominees, { loading: loadingSave }] = useMutation<
    SaveAssessmentNomineesMutation,
    SaveAssessmentNomineesMutationVariables
  >(saveAssessmentNomineesMutation);

  const handleSaveNominations = () => {
    saveAssessmentNominees({
      variables: {
        assessmentTemplateId: assessmentNomination.assessmentTemplate.id,
        complianceProgramId: assessmentNomination.complianceProgram.id,
        targetId: assessmentNomination.targetUser.id,
        responderIds: nomineeResponderIds,
      },
      context: { errorMatches: nominationRefusals },
      onCompleted: () => {
        onSaved?.();
        onClose();
      },
      // The error link shows the toast. Passing onError also stops the call
      // rejecting with nobody to catch it.
      onError: () => {},
    });
  };

  const handleChangeUserComboboxValue = (option: UserComboboxOption) => {
    if (option.type === "user") {
      setQuery("");
      setNominees([
        ...nominees,
        {
          id: option.id,
          name: option.name,
          avatar: option.avatar,
          activeAssessmentNominationCount:
            option.activeAssessmentNominationCount || 0,
          deactivated: false,
        },
      ]);
    }
  };

  const handleRemoveNomination = (nomineeId: number) => {
    setNominees(nominees.filter(({ id }) => id !== nomineeId));
  };

  return (
    <Modal onClose={onClose} open alignedTop>
      <div className="p-6 flex flex-col gap-6">
        <div>
          <ModalTitle onClose={onClose}>
            Nominations: {assessmentNomination.complianceProgram.title}
          </ModalTitle>
          <div className="text-xs mt-1 text-gray-500">
            For{" "}
            <span className="font-medium">
              {assessmentNomination.targetUser.name}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="font-medium">
            Nominees ({nomineesWithoutTarget.length})
          </div>
          <div className="text-sm">
            {loading ? (
              <Loading>Loading nominees</Loading>
            ) : error || !loaded ? (
              <div className="text-red-600">
                The nominees could not be loaded. Close this window and try
                again.
              </div>
            ) : nomineesWithoutTarget.length > 0 ? (
              <div className="divide-y border-t border-b">
                {nomineesWithoutTarget.map((nominee) => (
                  <div
                    key={nominee.id}
                    className="py-1.5 flex items-center gap-1 justify-between"
                  >
                    <div className="flex items-center gap-1.5 pl-1">
                      <Avatar user={nominee} size={4} />
                      {nominee.name}
                      {nominee.deactivated && <UserComboboxDeactivatedIcon />}
                      {nominee.activeAssessmentNominationCount !== null && (
                        <span className="text-gray-400">
                          ({nominee.activeAssessmentNominationCount}{" "}
                          {pluralize(
                            "nomination",
                            nominee.activeAssessmentNominationCount,
                          )}
                          )
                        </span>
                      )}
                    </div>
                    <Button
                      size={ButtonSize.medium}
                      icon={XIcon}
                      tooltip="Remove nominee"
                      theme={buttonTheme.iconGray}
                      onClick={() => handleRemoveNomination(nominee.id)}
                    />
                  </div>
                ))}
              </div>
            ) : null}
            {loaded && (
              <div className="max-w-96 mt-3">
                <UserCombobox
                  loading={comboboxLoading}
                  value={null}
                  options={options}
                  onChangeValue={handleChangeUserComboboxValue}
                  onChangeQuery={setQuery}
                  query={query}
                  placeholder="Select a nominee"
                  showUserActiveAssessmentNominationCount
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Button
            theme={buttonTheme.primary}
            text="Save nominations"
            onClick={handleSaveNominations}
            disabled={
              !loaded || loadingSave || isEqual(initialNominees, nominees)
            }
          />
          <Button text="Close" onClick={onClose} />
          {loadingSave && <Loading mini size={4} />}
        </div>
      </div>
    </Modal>
  );
};

export default AssessmenNominationtDialog;
